import { useRef } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';
import type {
	EXPERIMENT_REVENUE_MEMBERSHIP_IDENTIFIED_LOGIN_GATE,
	ResolvedExperimentData,
} from '@change-corgi/experiments/configs';

import { treatExperiment } from '#src/app/shared/api/experiments';
import { useLoginState } from '#src/app/shared/hooks/session';
import { useTrackExperimentExposure } from '#src/app/shared/hooks/tracking';
import type { PaymentType } from '#src/app/shared/utils/payments';

import type { VaultLoginGateContext } from '../useRequireAuthenticationToVault';
import { useRequireAuthenticationToVault } from '../useRequireAuthenticationToVault';

type IdentifiedLoginGateExperiment = ResolvedExperimentData<typeof EXPERIMENT_REVENUE_MEMBERSHIP_IDENTIFIED_LOGIN_GATE>;

type Props = { experiment: IdentifiedLoginGateExperiment; gateContext: VaultLoginGateContext };
type GateParams = {
	email: string;
	path: string;
	googleAuthEnabled?: boolean;
	paymentType: PaymentType;
};
/**
 * The membership pages' vault gate for identified sessions, behind the
 * revenue_membership_identified_login_gate experiment. Resolves true when the submit may go on.
 * Authenticated and guest sessions pass without being treated. An identified session is treated and
 * exposed at most once per mount, then either asked to log in (`variant_1`, see
 * useRequireAuthenticationToVault: true on login, false otherwise) or let through as before the gate
 * existed (`control`). Whenever the experiment does not resolve to a real assignment (disabled, not
 * found, killed, bot traffic or a failed lookup) it also passes, untreated: only `variant_1` ever gates,
 * and the server-side checks are governed separately by identified_saved_payment_method_enforcement.
 */
export function useIdentifiedLoginGate({ experiment, gateContext }: Props): (params: GateParams) => Promise<boolean> {
	const utilityContext = useUtilityContext();
	const loginState = useLoginState();
	const requireAuthenticationToVault = useRequireAuthenticationToVault();
	const trackExperimentExposure = useTrackExperimentExposure({ ...experiment, treated: true });
	const treatmentRef = useRef<Promise<void> | null>(null);
	const { experimentName, variation, fallback, override } = experiment;

	return async ({ paymentType, ...params }) => {
		if (loginState !== 'IDENTIFIED' || fallback) return true;
		if (!override) {
			treatmentRef.current ??= (async () => {
				if (await treatExperiment(utilityContext, experimentName)) await trackExperimentExposure();
			})();
			await treatmentRef.current;
		}
		if (variation === 'control') return true;
		return requireAuthenticationToVault({ ...params, tracking: { gateContext, paymentType } });
	};
}
